import React, { useState, useEffect } from 'react';
import { Button, Card, Col, Empty, notification, Popconfirm, Row, Tooltip, Typography } from 'antd';
import moment from 'moment/moment';
import { useArticles } from 'hooks/useArticles';
import { AddArticlePayload, Article } from 'types/Document';
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  ExpandOutlined,
  EyeInvisibleOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { ArticleView } from './ArticleView';
import './Article.scss';
import { ArticleForm } from './ArticleForm';
import { ACLCheck } from 'shared/Helpers';
import { useTags } from 'api/document';

interface ArticleCardProps {
  article: Article;
  setSelectedVendor: (id: number) => void;
  setSelectedTag: (id: number) => void;
  hideActions?: boolean;
}

export const ArticleCard: React.FC<ArticleCardProps> = ({
  article,
  setSelectedVendor,
  setSelectedTag,
  hideActions
}) => {
  const { allCategories, useEditArticle, useEditArticlePublicationDate, useDeleteArticle, getArticleCategory } =
    useArticles();
  const { data: tags, refetch: refetchTags } = useTags();

  const [isViewClicked, setIsViewClicked] = useState(false);
  const [articleToEdit, setArticleToEdit] = useState<number>();

  useEffect(() => {
    if (articleToEdit) {
      refetchTags().then();
    }
  }, [articleToEdit, refetchTags]);

  const { mutate: editArticle } = useEditArticle({
    onSuccess: () => {
      notification.success({
        message: 'Article edited successfully'
      });
      setArticleToEdit(undefined);
    },
    onError: () => {
      notification.error({
        message: 'Error while editing article'
      });
      setArticleToEdit(undefined);
    }
  });
  const { mutate: deleteArticle } = useDeleteArticle({
    onSuccess: () => {
      notification.success({
        message: 'Article deleted successfully'
      });
    },
    onError: () => {
      notification.error({
        message: 'Error while deleting article'
      });
    }
  });
  const { mutate: editArticlePublicationDate } = useEditArticlePublicationDate({
    onSuccess: (_data: Article) => {
      notification.success({
        message: `Article ${_data.publicationDate ? 'published' : 'unpublished'} successfully`
      });
      setArticleToEdit(undefined);
    },
    onError: () => {
      notification.error({
        message: 'Error while editing article'
      });
      setArticleToEdit(undefined);
    }
  });

  const onModalCancel = () => {
    setIsViewClicked(false);
  };

  const handleTagClick = (item: any) => {
    setSelectedTag(item.tag.id);
  };

  const handleVendorClick = (item: any) => {
    setSelectedVendor(item.vendor.id);
  };

  const renderArticleFile = () => {
    return <ArticleView article={article} open={isViewClicked} onCancel={onModalCancel} />;
  };

  const renderVendors = () => {
    return article.vendorFile.map((item) => (
      <Button className="p-0 h-auto" key={item.vendor.id} type="link" onClick={() => handleVendorClick(item)}>
        {item.vendor.name}
      </Button>
    ));
  };

  const renderTags = () => {
    return article.fileTag.map((item) => (
      <Button className="p-0 h-auto" key={item.tag.id} type="link" onClick={() => handleTagClick(item)}>
        {item.tag.name}
      </Button>
    ));
  };

  return (
    <div className="h-full">
      <Card
        className={`h-full pt-2 bg-gray-100 rounded-lg article-card ${!article?.publicationDate ? 'opacity-40' : ''}`}
        bordered={false}
      >
        {article ? (
          <>
            <Row justify="space-between" className="mb-4 opacity-50 w-full items-center article-info">
              <Col className="font-bold truncate flex-1 pr-3">{getArticleCategory(article)?.name}</Col>
              <Col>
                {/*{article.publication_date*/}
                {/*  ? moment().isAfter(article.publication_date)*/}
                {/*    ? moment(article?.publication_date).format('MM/DD/YY')*/}
                {/*    : `Scheduled - ${moment(article?.publication_date).format('MM/DD/YY')}`*/}
                {/*  : 'Unpublished'}*/}
              </Col>
            </Row>

            <div className="flex-1 w-full">
              <Typography.Title level={4} className="!mb-2 !font-semibold">
                {article.name}
              </Typography.Title>
              <p className="line-clamp-10 opacity-75 article-intro">{article.description}</p>
            </div>

            <div className="flex flex-wrap gap-x-2 mb-2 article-tags">
              {renderVendors()}
              {renderTags()}
            </div>
            <Row justify="space-between" align="middle" className="w-full article-actions">
              <Col className="whitespace-nowrap">
                {article.publicationDate ? (
                  <>
                    <Tooltip title="Download">
                      <Typography.Link
                        href={article.signedUrl}
                        download={`${article.filename}.pdf`}
                        className="p-2 inline-block"
                        target="_blank"
                      >
                        <DownloadOutlined className="text-xl" />
                      </Typography.Link>
                    </Tooltip>
                    <Tooltip title="View">
                      <Button
                        type="link"
                        className="p-2"
                        onClick={() => {
                          setIsViewClicked(true);
                        }}
                      >
                        <ExpandOutlined className="text-xl" />
                      </Button>
                    </Tooltip>
                  </>
                ) : null}
              </Col>
              {!hideActions && (
                <Col className="whitespace-nowrap">
                  {article.publicationDate && ACLCheck(['update:npi_docs']) ? (
                    <Button
                      type="link"
                      className="p-2"
                      onClick={() => {
                        setArticleToEdit(article.id);
                      }}
                    >
                      <EditOutlined className="text-xl" />
                    </Button>
                  ) : null}
                  {ACLCheck(['update:npi_docs']) ? (
                    <Popconfirm
                      title={`Are you sure to ${article.publicationDate ? 'unpublish' : 'publish'} this article?`}
                      onConfirm={() => {
                        editArticlePublicationDate({
                          data: {
                            publicationDate: article.publicationDate ? undefined : moment().toISOString()
                          },
                          id: article.id
                        });
                      }}
                      okText="Yes"
                      cancelText="No"
                    >
                      {article.publicationDate ? (
                        <EyeInvisibleOutlined className="text-xl inline-block p-2" />
                      ) : (
                        <EyeOutlined className="text-xl inline-block p-2" />
                      )}
                    </Popconfirm>
                  ) : null}
                  {ACLCheck(['delete:npi_docs']) ? (
                    <Popconfirm
                      title="Are you sure to delete this article?"
                      onConfirm={() => {
                        deleteArticle(article.id);
                      }}
                      okText="Yes"
                      cancelText="No"
                    >
                      <DeleteOutlined className="text-xl inline-block p-2 text-red-600" />
                    </Popconfirm>
                  ) : null}
                </Col>
              )}
            </Row>
          </>
        ) : (
          <Empty />
        )}
      </Card>
      {renderArticleFile()}
      {articleToEdit && ACLCheck(['update:npi_docs']) && (
        <ArticleForm
          tags={tags || []}
          allCategories={allCategories || []}
          onCancel={() => {
            setArticleToEdit(undefined);
          }}
          onSubmit={(payload: Partial<AddArticlePayload>) =>
            editArticle({
              data: payload,
              id: articleToEdit
            })
          }
          articleId={articleToEdit}
        />
      )}
    </div>
  );
};
